import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CONFIRM_DELETE } from '@opengov/stories-shared/constants';
import { RouteComponentProps } from '@reach/router';
import React, { useState } from 'react';

import { IPublicationViewMessages } from '../../../../../types';
import { styles } from './BulkDeleteReportEmbedModal.styles';

export type IBulkDeleteReportEmbedModalProps = RouteComponentProps & {
  show: boolean;
  messages: IPublicationViewMessages;
  selectedReportIds: { embedId: string; storyId: string }[];
  deleteReports(selectedReportIds: { storyId: string; embedId: string }[]): void;
  onClose?(): void;
};

export const BulkDeleteReportEmbedModal: React.FC<IBulkDeleteReportEmbedModalProps> = (props) => {
  const { deleteReports, selectedReportIds, messages, onClose, show } = props;
  const [inputTextValue, setInputTextValue] = useState('');

  if (!show) return null;

  const onConfirmDeleteReport = () => {
    void deleteReports(selectedReportIds);
    onClose();
  };

  const displayTitle = (
    selectedReportIds: { storyId: string; embedId: string }[],
    messages: IPublicationViewMessages
  ): string => {
    const reportCount = selectedReportIds && selectedReportIds.length;
    if (reportCount === 1) {
      return messages.deleteReportEmbed;
    } else if (reportCount > 1) {
      return messages.deleteReportEmbeds({ value: reportCount });
    }
    return '';
  };

  return (
    <Dialog open={show} onClose={onClose} data-test="bulk-delete-report" maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        {displayTitle(selectedReportIds, messages)}
        <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {messages.deleteReportEmbedDescription}
        </Typography>
        <FormControl fullWidth required>
          <FormLabel htmlFor="delete-confirmation-input">{messages.deleteConfirmation}</FormLabel>
          <TextField
            id="delete-confirmation-input"
            value={inputTextValue}
            onChange={(e) => setInputTextValue(e.target.value)}
            inputProps={{ 'data-test': 'delete-confirmation-input' }}
            size="small"
            sx={styles.textField}
          />
        </FormControl>
        <Box sx={styles.buttons}>
          <Button variant="outlined" data-test="cancel-delete-button" onClick={onClose}>
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="error"
            data-test="confirm-delete-button"
            disabled={inputTextValue !== CONFIRM_DELETE}
            onClick={onConfirmDeleteReport}
          >
            {messages.delete}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
